import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '../../../auth/useAuth0';
import { useTranslation } from 'react-i18next';
import { useBusinessContext } from '../../../context/BusinessContext';
import { useTimeFormat, formatClock, TimeFormat } from '../../../util/timeFormat';
import DateTimeField from '../../commons/datetime/DateTimeField';
import { useAlert } from '../../../context/alert/AlertContext';
import LoadingComponent from '../../commons/LoadingComponent';
import Drawer from '../../commons/drawer/Drawer';
import Pagination from '../../commons/pagination/Pagination';
import ConfirmModal from '../../commons/confirm-modal/ConfirmModal';
import {
    WaitlistEntry,
    WaitlistEntryInput,
    WaitlistPriority,
    WaitlistStatus,
    WAITLIST_PRIORITY_LABEL,
    WAITLIST_STATUS_LABEL,
} from '../../../model/waitlist/WaitlistEntry';
import {
    cancelWaitlistEntry,
    deleteWaitlistEntry,
    getWaitlistEntriesByBusiness,
    saveWaitlistEntry,
    scheduleWaitlistEntry,
} from '../../../service/waitlist/waitlist-service';
import { Client } from '../../../model/client/Client';
import { getAllClientsByBusinessId } from '../../../service/client/client-service';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';
import { Service } from '../../../model/service/Service';
import { getServicesByBusinessId } from '../../../service/service/service-service';
import { resolveTeamMemberColor } from '../../../util/teamMemberColor';
import { Appointment } from '../../../model/appointment/Appointment';
import { getAppointmentsByBusinessAndRange } from '../../../service/appointment/appointment-service';
import { BusinessHours } from '../../../model/business/BusinessHours';
import { findBusinessHoursByBusinessId } from '../../../service/business/business-service';
import { getMyPermissionGrant, PermissionGrant } from '../../../service/permission/permission-service';
import './WaitlistComponent.css';

const emptyDraft = (businessId: string): WaitlistEntryInput => ({
    businessId,
    serviceIds: [],
    status: 'OPEN',
    priority: 'MEDIUM',
});

const DOW = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const pad = (n: number) => String(n).padStart(2, '0');
const timeToMinutes = (hhmm: string): number => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + (m || 0);
};
const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();
/** Convert Date to "YYYY-MM-DDTHH:mm" in local time — matches FullCalendar / appointment drawer. */
const toLocalIso = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
/** "29/06 14:30" — compact slot label shown on the pills (date + time follow the
 *  active locale / business time format, not the browser's en default). */
const formatSlotLabel = (d: Date, fmt: TimeFormat = 'H24', locale = 'pt-BR') =>
    `${d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })} ${formatClock(d, fmt)}`;
/** "YYYY-MM-DD" in local time. */
const ymdOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** Monday of the week containing d (local midnight). */
const startOfWeekMon = (d: Date): Date => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dow = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - dow);
    return x;
};
/** "YYYY-MM-DD" → "DD/MM/YYYY" (empty for null/blank). */
const ymdToBR = (ymd?: string | null): string => {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-');
    return y && m && d ? `${d}/${m}/${y}` : '';
};

const WaitlistComponent: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { t, i18n } = useTranslation();
    // Date locale for the slot pills — follows the user's UI language, not the browser default.
    const dateLocale = (i18n.language || 'pt').startsWith('en') ? 'en-US' : 'pt-BR';
    const timeFmt = useTimeFormat();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    const businessIdNum = Number(selectedBusiness?.id) || 0;

    const [entries, setEntries] = useState<WaitlistEntry[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [businessHours, setBusinessHours] = useState<BusinessHours[]>([]);
    /** Business-wide appointments over the picker window (today .. +5 weeks),
     *  across ALL team members — so the slot finder works with no member picked
     *  (aggregate by service) as well as for a single member. */
    const [bizAppts, setBizAppts] = useState<Appointment[]>([]);
    /** Slot picker: selected day (YYYY-MM-DD) + the Monday of the shown week. */
    const [slotDay, setSlotDay] = useState<string>(() => ymdOf(new Date()));
    const [slotWeekStart, setSlotWeekStart] = useState<Date>(() => startOfWeekMon(new Date()));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    /** Filters / search / pagination. */
    const [statusFilter, setStatusFilter] = useState<WaitlistStatus | ''>('OPEN');
    const [tmFilter, setTmFilter] = useState<string>('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    /** Drawer state. */
    const [draft, setDraft] = useState<WaitlistEntryInput | null>(null);
    /** Local text for the dd/mm/aaaa deadline field (custom — no native format hint). */
    const [deadlineText, setDeadlineText] = useState('');
    /** The entry's PERSISTED status when the drawer opened — drives the read-only
     *  lock. Using this (not the live dropdown value) means changing the Status
     *  dropdown doesn't freeze the form mid-edit. */
    const [originalStatus, setOriginalStatus] = useState<WaitlistStatus | null>(null);
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    /** Spinner state for the "Schedule into this slot" button. */
    const [scheduling, setScheduling] = useState(false);
    /** Confirm modals. */
    const [cancelTarget, setCancelTarget] = useState<WaitlistEntry | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<WaitlistEntry | null>(null);
    const [actionBusy, setActionBusy] = useState(false);

    /** The caller's WAITLIST grant. canEdit gates create/edit/cancel/delete/
     *  schedule. null = not loaded → fail-closed (read-only). Backend re-checks. */
    const [waitlistGrant, setWaitlistGrant] = useState<PermissionGrant | null>(null);
    const canEditWaitlist = !!waitlistGrant?.canEdit;

    // ---------- Load ----------

    const reload = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const [data, cs, tm, svc, bh] = await Promise.all([
                getWaitlistEntriesByBusiness(businessId, token),
                getAllClientsByBusinessId(businessId, token).catch(() => [] as Client[]),
                getTeamMembersByBusinessId(businessId, token).catch(() => [] as TeamMember[]),
                getServicesByBusinessId(businessId, token).catch(() => [] as Service[]),
                businessIdNum
                    ? findBusinessHoursByBusinessId(businessIdNum, token).catch(() => [] as BusinessHours[])
                    : Promise.resolve([] as BusinessHours[]),
            ]);
            setEntries(data);
            setClients(cs);
            setMembers(tm);
            setServices(svc);
            setBusinessHours(bh);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('waitlist.alerts.failLoad'));
        } finally {
            setLoading(false);
        }
    }, [businessId, businessIdNum, getAccessTokenSilently, setAlert, t]);

    useEffect(() => { reload(); }, [reload]);

    // Load the caller's WAITLIST grant to gate write actions (fail-closed).
    useEffect(() => {
        if (!businessId) { setWaitlistGrant(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const grant = await getMyPermissionGrant(businessId, 'WAITLIST', token);
                if (!cancelled) setWaitlistGrant(grant);
            } catch {
                if (!cancelled) setWaitlistGrant(null);
            }
        })();
        return () => { cancelled = true; };
    }, [businessId, getAccessTokenSilently]);

    /** Pull every team member's appointments over the picker window so the slot
     *  finder can compute availability per member (and aggregate across all of
     *  them when no member is picked). Loaded once when the drawer opens. */
    useEffect(() => {
        if (!draft || !businessId) { setBizAppts([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const start = new Date(); start.setHours(0, 0, 0, 0);
                const end = new Date(start); end.setDate(end.getDate() + 35);
                // Backend expects a local-time string "yyyy-MM-dd'T'HH:mm:ss" (no Z).
                const list = await getAppointmentsByBusinessAndRange(
                    businessId, `${toLocalIso(start)}:00`, `${toLocalIso(end)}:00`, token);
                if (!cancelled) setBizAppts(list);
            } catch {
                if (!cancelled) setBizAppts([]);
            }
        })();
        return () => { cancelled = true; };
        // Only refetch when the drawer opens/closes (draft toggles null↔set), not on edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft?.id, draft === null, businessId, getAccessTokenSilently]);

    // ---------- Derived ----------

    /** Run the filters + sort by priority/added. */
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return entries
            // NB: validUntil is now the OFFER confirmation deadline (only meaningful
            // while OFFERED, enforced server-side), NOT a list-expiry — so OPEN
            // entries are never auto-expired here.
            .filter((e) => !statusFilter || e.status === statusFilter)
            .filter((e) => !tmFilter || String(e.teamMemberId) === tmFilter)
            .filter((e) => {
                if (!q) return true;
                const c = clients.find((x) => String(x.id) === String(e.clientId));
                const name = (c
                    ? `${c.firstName ?? ''} ${c.lastName ?? ''}`
                    : e.customerName ?? '').toLowerCase();
                return name.includes(q);
            })
            .sort((a, b) => {
                // Priority first (CRITICAL → LOW), then most recently added.
                const pa = WAITLIST_PRIORITY_LABEL[a.priority]?.weight ?? 2;
                const pb = WAITLIST_PRIORITY_LABEL[b.priority]?.weight ?? 2;
                if (pa !== pb) return pa - pb;
                return (b.createdOn ?? '').localeCompare(a.createdOn ?? '');
            });
    }, [entries, statusFilter, tmFilter, search, clients]);

    const pageItems = useMemo(
        () => filtered.slice((page - 1) * perPage, page * perPage),
        [filtered, page, perPage]
    );

    const memberLabel = (id?: string | null) => {
        if (!id) return <span className="text-muted">{t('waitlist.anyone')}</span>;
        const m = members.find((tm) => String(tm.id) === String(id));
        if (!m) return <span className="text-muted">{t('waitlist.unknownMember')}</span>;
        const idx = members.findIndex((tm) => tm.id === m.id);
        const color = resolveTeamMemberColor(m, idx);
        return (
            <span className="waitlist-tm-pill">
                <span className="waitlist-tm-pill__dot" style={{ background: color }} />
                {m.firstName} {m.lastName ?? ''}
            </span>
        );
    };
    const serviceName = (ids: string[]) => {
        if (!ids || ids.length === 0) return <span className="text-muted">—</span>;
        const first = services.find((s) => String(s.id) === String(ids[0]));
        const label = first?.name ?? t('waitlist.unknownService');
        const extra = ids.length > 1 ? ` +${ids.length - 1}` : '';
        return <>{label}{extra}</>;
    };
    const clientLabel = (e: WaitlistEntry) => {
        if (e.clientId) {
            const c = clients.find((x) => String(x.id) === String(e.clientId));
            if (c) return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || 'Client';
        }
        return e.customerName || '—';
    };

    // ---------- Drawer + actions ----------

    /** Reset the slot picker to today / this week whenever the drawer opens. */
    const resetSlotView = () => {
        setSlotDay(ymdOf(new Date()));
        setSlotWeekStart(startOfWeekMon(new Date()));
    };

    const openCreate = () => {
        if (!businessId) {
            setAlert('danger', t('waitlist.alerts.noBusiness'));
            return;
        }
        resetSlotView();
        setDeadlineText('');
        setOriginalStatus('OPEN');
        setDraft(emptyDraft(businessId));
    };

    const openEdit = (e: WaitlistEntry) => {
        resetSlotView();
        setDeadlineText(ymdToBR(e.validUntil));
        setOriginalStatus(e.status);
        setDraft({
            id: e.id,
            businessId: e.businessId,
            clientId: e.clientId ?? null,
            customerName: e.customerName ?? '',
            customerPhone: e.customerPhone ?? '',
            customerEmail: e.customerEmail ?? '',
            teamMemberId: e.teamMemberId ?? null,
            serviceIds: e.serviceIds ?? [],
            validUntil: e.validUntil ?? null,
            desiredStartTime: e.desiredStartTime ?? null,
            desiredEndTime: e.desiredEndTime ?? null,
            preferredTimes: e.preferredTimes ?? '',
            notes: e.notes ?? '',
            status: e.status,
            priority: e.priority ?? 'MEDIUM',
        });
    };

    const closeDrawer = () => { setDraft(null); setShowClientDropdown(false); setDeadlineText(''); };
    const updateDraft = (patch: Partial<WaitlistEntryInput>) => {
        setDraft((d) => (d ? { ...d, ...patch } : d));
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!canEditWaitlist) {
            setAlert('danger', t('waitlist.alerts.noPermission'));
            return;
        }
        if (!draft.clientId && !(draft.customerName ?? '').trim()) {
            setAlert('danger', t('waitlist.alerts.customerRequired'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await saveWaitlistEntry(
                {
                    ...draft,
                    customerName: draft.customerName?.trim() || null,
                    customerPhone: draft.customerPhone?.trim() || null,
                    customerEmail: draft.customerEmail?.trim() || null,
                    preferredTimes: draft.preferredTimes?.trim() || null,
                    notes: draft.notes?.trim() || null,
                    validUntil: draft.validUntil || null,
                },
                token
            );
            setAlert('success', draft.id ? t('waitlist.alerts.updated') : t('waitlist.alerts.saved'));
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('waitlist.alerts.failSave'));
        } finally {
            setSaving(false);
        }
    };

    /** Save the entry AND offer the chosen slot in one step — persists the entry
     *  (create or update), then creates the AWAITING_CONFIRMATION appointment and
     *  flips it to OFFERED. The client then confirms/declines. */
    const handleSaveAndOffer = async () => {
        if (!draft) return;
        if (!canEditWaitlist) {
            setAlert('danger', t('waitlist.alerts.noPermission'));
            return;
        }
        if (!draft.clientId && !(draft.customerName ?? '').trim()) {
            setAlert('danger', t('waitlist.alerts.customerRequired'));
            return;
        }
        if (!draft.serviceIds || draft.serviceIds.length === 0) {
            setAlert('danger', t('waitlist.alerts.noServiceForSlot'));
            return;
        }
        if (!draft.teamMemberId || !draft.desiredStartTime || !draft.desiredEndTime) {
            setAlert('danger', t('waitlist.alerts.noSlotTime'));
            return;
        }
        try {
            setScheduling(true);
            const token = await getAccessTokenSilently();
            const saved = await saveWaitlistEntry(
                {
                    ...draft,
                    customerName: draft.customerName?.trim() || null,
                    customerPhone: draft.customerPhone?.trim() || null,
                    customerEmail: draft.customerEmail?.trim() || null,
                    preferredTimes: draft.preferredTimes?.trim() || null,
                    notes: draft.notes?.trim() || null,
                    validUntil: draft.validUntil || null,
                },
                token
            );
            const appt = await scheduleWaitlistEntry(
                saved.id, draft.desiredStartTime, draft.desiredEndTime, token);
            setAlert('success', t('waitlist.alerts.offered', { id: appt.id }));
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('waitlist.alerts.failSchedule'));
        } finally {
            setScheduling(false);
        }
    };

    const confirmCancel = async () => {
        if (!cancelTarget) return;
        if (!canEditWaitlist) {
            setAlert('danger', t('waitlist.alerts.noPermission'));
            setCancelTarget(null);
            return;
        }
        try {
            setActionBusy(true);
            const token = await getAccessTokenSilently();
            await cancelWaitlistEntry(cancelTarget.id, token);
            setAlert('success', t('waitlist.alerts.cancelled'));
            setCancelTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('waitlist.alerts.failCancel'));
        } finally {
            setActionBusy(false);
        }
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        if (!canEditWaitlist) {
            setAlert('danger', t('waitlist.alerts.noPermission'));
            setDeleteTarget(null);
            return;
        }
        try {
            setActionBusy(true);
            const token = await getAccessTokenSilently();
            await deleteWaitlistEntry(deleteTarget.id, token);
            setAlert('success', t('waitlist.alerts.deleted'));
            setDeleteTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('waitlist.alerts.failDelete'));
        } finally {
            setActionBusy(false);
        }
    };

    /** Client autocomplete — matches the calendar appointment drawer pattern. */
    const clientMatches = useMemo(() => {
        if (!draft) return [] as Client[];
        const q = (draft.customerName ?? '').trim().toLowerCase();
        if (q.length < 2 || draft.clientId) return [];
        return clients
            .filter((c) => {
                const full = `${c.firstName ?? ''} ${c.lastName ?? ''}`.toLowerCase();
                return (
                    full.includes(q) ||
                    (c.email ?? '').toLowerCase().includes(q) ||
                    (c.phone ?? '').toLowerCase().includes(q)
                );
            })
            .slice(0, 6);
    }, [draft, clients]);

    const linkClient = (c: Client) => {
        updateDraft({
            clientId: String(c.id),
            customerName: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim(),
            customerPhone: c.phone ?? '',
            customerEmail: c.email ?? '',
        });
        setShowClientDropdown(false);
    };
    const clearLinkedClient = () => updateDraft({ clientId: null });

    // Service options scoped to the picked team member (mirrors appointment drawer).
    const allowedServicesForDraft = useMemo(() => {
        if (!draft?.teamMemberId) return services;
        const tm = members.find((m) => String(m.id) === String(draft.teamMemberId));
        const ids = tm?.serviceIds ?? [];
        if (ids.length === 0) return services;
        return services.filter((s) => ids.includes(s.id));
    }, [draft?.teamMemberId, members, services]);

    // The single picked service (we store an array but the form is single-pick
    // to match the appointment-edit screen).
    const pickedServiceId = (draft?.serviceIds && draft.serviceIds[0]) || '';

    const pickedTeamMember = useMemo(
        () => members.find((m) => String(m.id) === String(draft?.teamMemberId)),
        [members, draft?.teamMemberId]
    );

    /** Non-OPEN entries freeze their fields (editing would desync the linked
     *  appointment). EXCEPTION: an OFFERED entry can still have its confirmation
     *  deadline (validUntil) postponed — so only the deadline is editable there.
     *  Re-checked server-side. */
    const fieldsLocked = !!draft?.id && !!originalStatus && originalStatus !== 'OPEN';   // all fields but deadline
    const fullyLocked = fieldsLocked && originalStatus !== 'OFFERED';                     // deadline too (CONVERTED/EXPIRED/CANCELLED)

    /** A concrete slot is chosen (time + provider) — enables "Save & offer". */
    const slotSelected = !!(draft?.desiredStartTime && draft?.desiredEndTime && draft?.teamMemberId);

    /** Bounds for the confirmation deadline: today .. +5 years. Stops absurd years
     *  (e.g. 9202) and past deadlines. */
    const todayYmd = ymdOf(new Date());
    const maxDeadlineYmd = (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 5); return ymdOf(d); })();

    /** True when the typed deadline is a complete date that is invalid or out of
     *  range — surfaced inline so the value isn't silently dropped. */
    const deadlineInvalid = deadlineText.replace(/\D/g, '').length === 8 && !draft?.validUntil;

    /** dd/mm/aaaa text → auto-mask + parse to validUntil (YYYY-MM-DD). Only sets a
     *  value when the date is complete, real, and within [today, +1 year]. */
    const onDeadlineInput = (raw: string) => {
        const digits = raw.replace(/\D/g, '').slice(0, 8); // ddmmyyyy
        let txt = digits;
        if (digits.length > 4) txt = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        else if (digits.length > 2) txt = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        setDeadlineText(txt);
        if (digits.length === 8) {
            const dd = digits.slice(0, 2), mm = digits.slice(2, 4), yyyy = digits.slice(4);
            const ymd = `${yyyy}-${mm}-${dd}`;
            const dNum = Number(dd), mNum = Number(mm);
            const realDate = new Date(`${ymd}T00:00:00`);
            const isReal = !Number.isNaN(realDate.getTime())
                && realDate.getDate() === dNum && realDate.getMonth() + 1 === mNum;
            const inRange = ymd >= todayYmd && ymd <= maxDeadlineYmd;
            updateDraft({ validUntil: (dNum >= 1 && dNum <= 31 && mNum >= 1 && mNum <= 12 && isReal && inRange) ? ymd : null });
        } else {
            updateDraft({ validUntil: null });
        }
    };

    /**
     * Same slot-finder shape used by the calendar appointment drawer —
     * intersection of team-member working hours and business hours, minus the
     * unbookable break interval, minus existing non-cancelled appointments,
     * clamped to "now" for today.
     */
    const computeFreeIntervals = useCallback((
        candidateDate: Date,
        member: TeamMember
    ): [number, number][] | null => {
        const dayKey = DOW[candidateDate.getDay()];
        const wh = (member.workingHours ?? []).find((w) => w.dayOfWeek === dayKey);
        if (!wh) return null;
        let dayStart = timeToMinutes(wh.startTime);
        let dayEnd = timeToMinutes(wh.endTime);
        if (businessHours.length > 0) {
            const bizDay = businessHours.find((b) => b.dayOfWeek === dayKey);
            if (!bizDay) return null;
            dayStart = Math.max(dayStart, timeToMinutes(bizDay.openTime));
            dayEnd = Math.min(dayEnd, timeToMinutes(bizDay.closeTime));
            if (dayStart >= dayEnd) return null;
        }
        let intervals: [number, number][];
        if (!wh.allowBookingOnIntervalTime && wh.startIntervalTime && wh.endIntervalTime) {
            const bStart = timeToMinutes(wh.startIntervalTime);
            const bEnd = timeToMinutes(wh.endIntervalTime);
            const cbStart = Math.max(bStart, dayStart);
            const cbEnd = Math.min(bEnd, dayEnd);
            intervals = [];
            if (dayStart < cbStart) intervals.push([dayStart, cbStart]);
            if (cbEnd < dayEnd) intervals.push([cbEnd, dayEnd]);
            if (intervals.length === 0) intervals = [[dayStart, dayEnd]];
        } else {
            intervals = [[dayStart, dayEnd]];
        }
        // Subtract THIS member's booked appointments on that day (from the
        // business-wide list, filtered by member).
        const dayStr = candidateDate.toDateString();
        const dayApts = bizAppts.filter(
            (a) => String(a.teamMemberId) === String(member.id)
                && new Date(a.startTime).toDateString() === dayStr
                && a.status !== 'CANCELLED' && a.status !== 'DECLINED'
        );
        for (const a of dayApts) {
            const aStart = minutesOfDay(new Date(a.startTime));
            const aEnd = minutesOfDay(new Date(a.endTime));
            const next: [number, number][] = [];
            for (const [s, e] of intervals) {
                if (aEnd <= s || aStart >= e) next.push([s, e]);
                else {
                    if (aStart > s) next.push([s, aStart]);
                    if (aEnd < e) next.push([aEnd, e]);
                }
            }
            intervals = next;
        }
        const now = new Date();
        const earliest = candidateDate.toDateString() === now.toDateString() ? minutesOfDay(now) : 0;
        return intervals
            .map(([s, e]) => [Math.max(s, earliest), e] as [number, number])
            .filter(([s, e]) => e > s);
    }, [businessHours, bizAppts]);

    /** Picked service's duration (or 60min default). */
    const pickedServiceDuration = useMemo(() => {
        const svc = services.find((s) => String(s.id) === String(draft?.serviceIds?.[0]));
        return svc?.totalDuration && svc.totalDuration > 0 ? svc.totalDuration : 60;
    }, [services, draft?.serviceIds]);

    /** Members the slots are computed for: the picked one, or — when none is
     *  picked — every active member that offers the chosen service. */
    const candidateMembers = useMemo<TeamMember[]>(() => {
        if (pickedTeamMember) return [pickedTeamMember];
        if (!pickedServiceId) return [];
        return members.filter((m) => (m.serviceIds ?? []).map(String).includes(String(pickedServiceId)));
    }, [pickedTeamMember, members, pickedServiceId]);

    /** Open slots of the picked-service duration on `candidateDate`, across all
     *  candidate members, each tagged with its member. 15-min grid; deduped by
     *  member+time; sorted by time then member name. */
    const findSlotsOnDay = useCallback((candidateDate: Date) => {
        const INC = 15;
        const dur = pickedServiceDuration;
        const out: { start: Date; end: Date; memberId: string; memberName: string }[] = [];
        for (const member of candidateMembers) {
            const intervals = computeFreeIntervals(candidateDate, member);
            if (!intervals) continue;
            const roundUp = (m: number) => Math.ceil(m / INC) * INC;
            const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
            for (const [s, e] of intervals) {
                for (let tm = roundUp(s); tm + dur <= e; tm += INC) {
                    const start = new Date(candidateDate);
                    start.setHours(Math.floor(tm / 60), tm % 60, 0, 0);
                    out.push({
                        start,
                        end: new Date(start.getTime() + dur * 60_000),
                        memberId: String(member.id),
                        memberName: name,
                    });
                }
            }
        }
        out.sort((a, b) =>
            a.start.getTime() - b.start.getTime() || a.memberName.localeCompare(b.memberName));
        return out;
    }, [candidateMembers, computeFreeIntervals, pickedServiceDuration]);

    /** Free slots on the selected day (empty for past days). */
    const selectedDaySlots = useMemo(() => {
        if (!slotDay || slotDay < ymdOf(new Date())) return [];
        return findSlotsOnDay(new Date(`${slotDay}T12:00:00`));
    }, [slotDay, findSlotsOnDay]);

    /** The 7 days of the shown week, flagged today/past + whether each has a slot. */
    const slotWeekDays = useMemo(() => {
        const todayY = ymdOf(new Date());
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(slotWeekStart);
            d.setDate(slotWeekStart.getDate() + i);
            const y = ymdOf(d);
            return {
                ymd: y,
                weekday: d.toLocaleDateString(dateLocale, { weekday: 'short' }),
                dayNum: d.getDate(),
                isToday: y === todayY,
                isPast: y < todayY,
                hasSlots: y >= todayY && findSlotsOnDay(new Date(`${y}T12:00:00`)).length > 0,
            };
        });
    }, [slotWeekStart, findSlotsOnDay, dateLocale]);

    /** "12 – 18/06" label for the shown week. */
    const slotRangeLabel = useMemo(() => {
        const last = new Date(slotWeekStart);
        last.setDate(last.getDate() + 6);
        const fmt = (d: Date) => d.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' });
        return `${fmt(slotWeekStart)} – ${fmt(last)}`;
    }, [slotWeekStart, dateLocale]);

    const canSlotPrevWeek = ymdOf(slotWeekStart) > ymdOf(startOfWeekMon(new Date()));
    const shiftSlotWeek = (deltaDays: number) =>
        setSlotWeekStart((prev) => {
            const x = new Date(prev);
            x.setDate(x.getDate() + deltaDays);
            return x;
        });

    /** Select a slot — fills Start/End AND the team member who owns it (so an
     *  "Anyone" pick resolves to a concrete provider), and jumps the day view. */
    const pickSlot = (slot: { start: Date; end: Date; memberId: string }) => {
        updateDraft({
            desiredStartTime: toLocalIso(slot.start),
            desiredEndTime: toLocalIso(slot.end),
            teamMemberId: slot.memberId,
        });
    };

    /** "Find next" button — first available slot across the next 35 days (any
     *  candidate member). Fills Start/End + member and jumps to that day. */
    const findNextSlot = () => {
        if (!pickedServiceId) {
            setAlert('danger', t('waitlist.alerts.noServiceForSlot'));
            return;
        }
        if (candidateMembers.length === 0) {
            setAlert('danger', t('waitlist.alerts.noMembersForService'));
            return;
        }
        for (let off = 0; off < 35; off++) {
            const d = new Date(); d.setDate(d.getDate() + off);
            const slots = findSlotsOnDay(new Date(`${ymdOf(d)}T12:00:00`));
            if (slots.length > 0) {
                const slot = slots[0];
                pickSlot(slot);
                setSlotDay(ymdOf(slot.start));
                setSlotWeekStart(startOfWeekMon(slot.start));
                setAlert('success', t('waitlist.alerts.slotSuggested', { slot: formatSlotLabel(slot.start, timeFmt, dateLocale) }));
                return;
            }
        }
        setAlert('danger', t('waitlist.alerts.noSlotsAny'));
    };

    // ---------- Render ----------

    if (loading && entries.length === 0) return <LoadingComponent />;

    return (
        <div className="waitlist-page" data-cy="waitlist-page">
            <div className="list-card">
                <div className="list-header">
                    <h5 className="mb-0">{t('waitlist.title')}</h5>
                    <div className="gs-list-search">
                        <span className="gs-list-search__icon" aria-hidden>⌕</span>
                        <input
                            type="search"
                            className="form-control form-control-sm"
                            data-cy="waitlist-search-input"
                            placeholder={t('waitlist.searchPlaceholder')}
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                        {search && (
                            <button
                                type="button"
                                className="gs-list-search__clear"
                                onClick={() => { setSearch(''); setPage(1); }}
                                aria-label={t('common.clear')}
                                title={t('common.clear')}
                            >×</button>
                        )}
                    </div>
                    {canEditWaitlist && (
                        <button type="button" className="btn btn-primary btn-sm" data-cy="waitlist-add-btn" onClick={openCreate}>
                            {t('waitlist.addNew')}
                        </button>
                    )}
                </div>

                {/* Inline filters — match Appointments toolbar style. */}
                <div className="waitlist-filters">
                    <label>
                        {t('waitlist.filters.status')}
                        <select
                            data-cy="waitlist-filter-status"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value as WaitlistStatus | ''); setPage(1); }}
                        >
                            <option value="">{t('waitlist.filters.allStatus')}</option>
                            {(Object.keys(WAITLIST_STATUS_LABEL) as WaitlistStatus[]).map((k) => (
                                <option key={k} value={k}>{t(`waitlist.status.${k}`, { defaultValue: WAITLIST_STATUS_LABEL[k].label })}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        {t('waitlist.filters.teamMember')}
                        <select
                            data-cy="waitlist-filter-team-member"
                            value={tmFilter}
                            onChange={(e) => { setTmFilter(e.target.value); setPage(1); }}
                        >
                            <option value="">{t('waitlist.filters.allMembers')}</option>
                            {members.map((m) => (
                                <option key={m.id} value={String(m.id)}>
                                    {m.firstName} {m.lastName ?? ''}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div className="waitlist-filters__count">
                        {t('waitlist.entriesCount')} <strong>{filtered.length}</strong>
                    </div>
                </div>

                <div className="waitlist-table-wrap">
                    <table className="waitlist-table" data-cy="waitlist-table">
                        <thead>
                            <tr>
                                <th>{t('waitlist.table.customer')}</th>
                                <th>{t('waitlist.table.teamMember')}</th>
                                <th>{t('waitlist.table.service')}</th>
                                <th>{t('waitlist.table.priority')}</th>
                                <th>{t('waitlist.table.validUntil')}</th>
                                <th>{t('waitlist.table.status')}</th>
                                <th>{t('waitlist.table.added')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.length === 0 && (
                                <tr><td colSpan={8} className="empty-hint">{t('waitlist.table.empty')}</td></tr>
                            )}
                            {pageItems.map((e) => {
                                const sMeta = WAITLIST_STATUS_LABEL[e.status];
                                const pMeta = WAITLIST_PRIORITY_LABEL[e.priority];
                                const added = e.createdOn ? new Date(e.createdOn) : null;
                                const isExpired = e.status === 'EXPIRED';
                                return (
                                    <tr
                                        key={e.id}
                                        onClick={() => openEdit(e)}
                                        className={`waitlist-row ${isExpired ? 'waitlist-row--expired' : ''}`}
                                        data-cy={`waitlist-row-${e.id}`}
                                    >
                                        <td>
                                            <div className="waitlist-row__customer">{clientLabel(e)}</div>
                                            {e.customerPhone && (
                                                <div className="waitlist-row__sub">{e.customerPhone}</div>
                                            )}
                                        </td>
                                        <td>{memberLabel(e.teamMemberId)}</td>
                                        <td>{serviceName(e.serviceIds)}</td>
                                        <td>
                                            <span className={`waitlist-priority waitlist-priority--${pMeta?.tone ?? 'medium'}`}>
                                                {t(`waitlist.priority.${e.priority}`, { defaultValue: pMeta?.label ?? e.priority })}
                                            </span>
                                        </td>
                                        <td>
                                            {e.validUntil
                                                ? new Date(`${e.validUntil}T00:00:00`).toLocaleDateString(dateLocale)
                                                : '—'}
                                        </td>
                                        <td>
                                            <span className={`waitlist-status waitlist-status--${sMeta.tone}`} data-cy="waitlist-status">
                                                {t(`waitlist.status.${e.status}`, { defaultValue: sMeta.label })}
                                            </span>
                                        </td>
                                        <td>
                                            {added
                                                ? added.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' })
                                                : '—'}
                                        </td>
                                        <td onClick={(ev) => ev.stopPropagation()}>
                                            {canEditWaitlist ? (
                                                <div className="waitlist-actions">
                                                    {e.status === 'OPEN' && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            data-cy="waitlist-cancel-btn"
                                                            onClick={() => setCancelTarget(e)}
                                                            title={t('waitlist.actions.cancelTitle')}
                                                        >
                                                            {t('waitlist.actions.cancel')}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        data-cy="waitlist-remove-btn"
                                                        onClick={() => setDeleteTarget(e)}
                                                        title={t('waitlist.actions.deleteTitle')}
                                                    >
                                                        {t('waitlist.actions.delete')}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    perPage={perPage}
                    totalItems={filtered.length}
                    onPageChange={setPage}
                    onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                    itemLabel="entry"
                />
            </div>

            {/* ----- Drawer: matches the calendar appointment drawer's form patterns. */}
            <Drawer
                open={!!draft}
                onClose={closeDrawer}
                title={draft?.id ? t('waitlist.drawer.titleEdit') : t('waitlist.drawer.titleNew')}
                size="md"
                footer={
                    draft && (
                        <>
                            {/* OPEN + a slot chosen → the single primary action is "Save &
                                offer". OPEN without a slot → plain "Save" (just add to the
                                list). OFFERED → "Save" (persists the postponed deadline).
                                Fully-locked → only Close. */}
                            {canEditWaitlist && draft.status === 'OPEN' && slotSelected ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    data-cy="waitlist-save-offer-btn"
                                    onClick={handleSaveAndOffer}
                                    disabled={saving || scheduling}
                                    title={t('waitlist.drawer.scheduleBtnTitle')}
                                >
                                    {scheduling ? t('waitlist.drawer.schedulingBtn') : t('waitlist.drawer.saveAndOffer')}
                                </button>
                            ) : (canEditWaitlist && !fullyLocked && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    data-cy="waitlist-save-btn"
                                    onClick={handleSave}
                                    disabled={saving || scheduling}
                                >
                                    {saving ? t('waitlist.drawer.saving') : t('waitlist.drawer.save')}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={closeDrawer}
                                disabled={saving || scheduling}
                            >
                                {canEditWaitlist && !fullyLocked ? t('common.cancel') : t('common.close')}
                            </button>
                        </>
                    )
                }
            >
                {draft && (
                    <form className="event-form" data-cy="waitlist-drawer" onSubmit={(e) => e.preventDefault()}>
                      {/* Non-OPEN entries freeze every field via this fieldset. The
                          confirmation-deadline field below is kept OUTSIDE it so an OFFERED
                          entry can still be postponed. Server re-checks on save. */}
                      <fieldset disabled={fieldsLocked} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                        {/* Priority + (when editing) Status — top row, like Status on the appointment drawer. */}
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>{t('waitlist.drawer.labelPriority')} <span className="gs-required">*</span></label>
                                <select
                                    className="form-select"
                                    data-cy="waitlist-priority-input"
                                    value={draft.priority ?? 'MEDIUM'}
                                    onChange={(e) => updateDraft({ priority: e.target.value as WaitlistPriority })}
                                >
                                    {(Object.keys(WAITLIST_PRIORITY_LABEL) as WaitlistPriority[]).map((k) => (
                                        <option key={k} value={k}>{t(`waitlist.priority.${k}`, { defaultValue: WAITLIST_PRIORITY_LABEL[k].label })}</option>
                                    ))}
                                </select>
                            </div>
                            {draft.id && (
                                <div className="col-md-6 form-group">
                                    <label>{t('waitlist.drawer.labelStatus')}</label>
                                    <select
                                        className="form-select"
                                        data-cy="waitlist-status-input"
                                        value={draft.status ?? 'OPEN'}
                                        onChange={(e) => updateDraft({ status: e.target.value as WaitlistStatus })}
                                    >
                                        {(Object.keys(WAITLIST_STATUS_LABEL) as WaitlistStatus[]).map((k) => (
                                            <option key={k} value={k}>{t(`waitlist.status.${k}`, { defaultValue: WAITLIST_STATUS_LABEL[k].label })}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Customer name with autocomplete + linked badge — mirrors the
                            appointment drawer behaviour the user already knows. */}
                        <div className="form-group position-relative">
                            <label>{t('waitlist.drawer.labelCustomer')} <span className="gs-required">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                data-cy="waitlist-customer-input"
                                placeholder={t('waitlist.drawer.customerPlaceholder')}
                                value={draft.customerName ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    updateDraft({
                                        customerName: v,
                                        // Editing unlinks unless it still matches the linked label.
                                        clientId: draft.clientId && (() => {
                                            const linked = clients.find((c) => String(c.id) === String(draft.clientId));
                                            const label = linked ? `${linked.firstName ?? ''} ${linked.lastName ?? ''}`.trim() : '';
                                            return label === v ? draft.clientId : null;
                                        })(),
                                    });
                                    setShowClientDropdown(true);
                                }}
                                onFocus={() => setShowClientDropdown(true)}
                                onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                                autoComplete="off"
                            />
                            {showClientDropdown && clientMatches.length > 0 && (
                                <div className="client-autocomplete">
                                    {clientMatches.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="client-autocomplete-item"
                                            onMouseDown={(e) => { e.preventDefault(); linkClient(c); }}
                                        >
                                            <div className="fw-semibold">{c.firstName} {c.lastName}</div>
                                            {(c.email || c.phone) && (
                                                <div className="small text-muted">
                                                    {c.email}{c.email && c.phone ? ' · ' : ''}{c.phone}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {draft.clientId && (
                                <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                    <span className="badge bg-success-subtle text-success-emphasis">
                                        {t('waitlist.drawer.linkedBadge')}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={clearLinkedClient}
                                    >
                                        {t('waitlist.drawer.unlink')}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelPhone')}</label>
                            <input
                                type="tel"
                                className="form-control"
                                data-cy="waitlist-phone-input"
                                value={draft.customerPhone ?? ''}
                                readOnly={!!draft.clientId}
                                onChange={(e) => updateDraft({ customerPhone: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelEmail')}</label>
                            <input
                                type="email"
                                className="form-control"
                                data-cy="waitlist-email-input"
                                value={draft.customerEmail ?? ''}
                                readOnly={!!draft.clientId}
                                onChange={(e) => updateDraft({ customerEmail: e.target.value })}
                            />
                        </div>

                        {/* Team member — same colored dot + select pattern as the
                            appointment drawer. */}
                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelTeamMember')}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {(() => {
                                    if (!pickedTeamMember) return null;
                                    const idx = members.findIndex((m) => m.id === pickedTeamMember.id);
                                    const c = resolveTeamMemberColor(pickedTeamMember, idx);
                                    return (
                                        <span style={{
                                            width: 18, height: 18, minWidth: 18,
                                            borderRadius: '50%',
                                            background: c,
                                            border: '1px solid #ccc',
                                            flexShrink: 0,
                                        }} aria-hidden />
                                    );
                                })()}
                                <select
                                    className="form-select"
                                    data-cy="waitlist-teammember-input"
                                    value={draft.teamMemberId ?? ''}
                                    onChange={(e) => updateDraft({ teamMemberId: e.target.value || null, serviceIds: [] })}
                                    style={{ flex: 1 }}
                                >
                                    <option value="">{t('waitlist.drawer.anyoneOption')}</option>
                                    {members.map((m) => (
                                        <option key={m.id} value={String(m.id)}>
                                            {m.firstName} {m.lastName ?? ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Single service select (same pattern as appointment drawer). */}
                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelService')}</label>
                            <select
                                className="form-select"
                                data-cy="waitlist-service-input"
                                value={pickedServiceId}
                                onChange={(e) =>
                                    updateDraft({ serviceIds: e.target.value ? [e.target.value] : [] })
                                }
                            >
                                <option value="">{t('waitlist.drawer.noneOption')}</option>
                                {allowedServicesForDraft.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}{s.totalDuration ? ` (${s.totalDuration} min)` : ''}
                                    </option>
                                ))}
                            </select>
                            {draft.teamMemberId && allowedServicesForDraft.length === 0 && (
                                <small className="text-danger">
                                    {t('waitlist.drawer.noServicesForMember')}
                                </small>
                            )}
                        </div>

                        {/* Client's preferred times — shown BEFORE the slot picker so staff can
                            honor the preference when choosing which slot to offer. */}
                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelPreferredTimes')}</label>
                            <textarea
                                className="form-control"
                                data-cy="waitlist-preferredtimes-input"
                                rows={2}
                                placeholder={t('waitlist.drawer.preferredTimesPlaceholder')}
                                value={draft.preferredTimes ?? ''}
                                onChange={(e) => updateDraft({ preferredTimes: e.target.value })}
                            />
                        </div>

                        {/* Availability picker — week navigation + per-day slots, across the
                            picked member OR every member offering the service when none is
                            picked. Mirrors the new-appointment drawer. Shown only for OPEN
                            entries (offering is the only action); picking a slot resolves the
                            team member. The actual "Save & offer" lives in the footer. */}
                        {pickedServiceId && draft.status === 'OPEN' && (
                            <>
                                <div className="form-group">
                                    <label className="small text-muted mb-1">{t('waitlist.drawer.nextSlotsLabel')}</label>

                                    <div className="wl-slotnav">
                                        <button type="button" className="btn btn-sm btn-light"
                                            disabled={!canSlotPrevWeek}
                                            onClick={() => shiftSlotWeek(-7)}
                                            aria-label={t('waitlist.drawer.prevWeek')}>‹</button>
                                        <span className="wl-slotnav__range">{slotRangeLabel}</span>
                                        <button type="button" className="btn btn-sm btn-light"
                                            onClick={() => shiftSlotWeek(7)}
                                            aria-label={t('waitlist.drawer.nextWeek')}>›</button>
                                    </div>

                                    <div className="wl-daystrip">
                                        {slotWeekDays.map((d) => (
                                            <button key={d.ymd} type="button"
                                                data-cy="waitlist-day-btn"
                                                className={`wl-day${d.ymd === slotDay ? ' is-active' : ''}`}
                                                disabled={d.isPast}
                                                onClick={() => setSlotDay(d.ymd)}>
                                                <span className="wl-day__wd">{d.weekday}</span>
                                                <span className="wl-day__n">{d.dayNum}</span>
                                                <span className={`wl-day__dot${d.hasSlots ? ' on' : ''}`} />
                                            </button>
                                        ))}
                                    </div>

                                    {selectedDaySlots.length > 0 ? (
                                        <div className="wl-slots" data-cy="waitlist-slots">
                                            {selectedDaySlots.map((slot, i) => {
                                                const isSel = draft.desiredStartTime === toLocalIso(slot.start)
                                                    && String(draft.teamMemberId) === slot.memberId;
                                                return (
                                                    <button key={i} type="button"
                                                        className={`btn btn-sm ${isSel ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => pickSlot(slot)}
                                                        title={slot.memberName}>
                                                        {formatClock(slot.start, timeFmt)}
                                                        {!pickedTeamMember && (
                                                            <span className="wl-slot__who"> · {slot.memberName}</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="form-text">
                                            {candidateMembers.length === 0
                                                ? t('waitlist.drawer.noMembersForService')
                                                : t('waitlist.drawer.noSlotsThisDay')}
                                        </div>
                                    )}
                                    <div className="form-text">{t('waitlist.drawer.nextSlotsHint')}</div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 form-group">
                                        <label>{t('waitlist.drawer.labelStart')}</label>
                                        <DateTimeField
                                            data-cy="waitlist-start-input"
                                            value={draft.desiredStartTime ?? ''}
                                            onChange={(v) =>
                                                updateDraft({ desiredStartTime: v || null })
                                            }
                                        />
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label>{t('waitlist.drawer.labelEnd')}</label>
                                        <DateTimeField
                                            data-cy="waitlist-end-input"
                                            value={draft.desiredEndTime ?? ''}
                                            onChange={(v) =>
                                                updateDraft({ desiredEndTime: v || null })
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        data-cy="waitlist-find-slot-btn"
                                        onClick={findNextSlot}
                                        disabled={saving}
                                    >
                                        {t('waitlist.drawer.findSlotBtn')}
                                    </button>
                                    <div className="form-text">
                                        {t('waitlist.drawer.findSlotHint')}
                                    </div>
                                </div>
                                {slotSelected && (
                                    <div className="form-text">{t('waitlist.drawer.scheduleHint')}</div>
                                )}
                            </>
                        )}

                        {/* Status notes for entries tied to an appointment. */}
                        {draft.id && draft.status === 'OFFERED' && (
                            <div className="form-text waitlist-schedule__offered">
                                {t('waitlist.drawer.offeredAwaiting')}
                            </div>
                        )}
                        {draft.id && draft.status === 'CONVERTED' && (
                            <div className="form-text waitlist-schedule__done">
                                {t('waitlist.drawer.alreadyConverted')}
                            </div>
                        )}
                      </fieldset>

                        {/* Confirmation deadline — OUTSIDE the freeze fieldset so an OFFERED
                            offer can still be postponed (disabled only when fully locked). */}
                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelValidUntil')}</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="form-control"
                                data-cy="waitlist-validuntil-input"
                                placeholder={t('waitlist.drawer.validUntilPlaceholder')}
                                value={deadlineText}
                                disabled={fullyLocked}
                                onChange={(e) => onDeadlineInput(e.target.value)}
                            />
                            {deadlineInvalid && (
                                <small className="text-danger d-block">{t('waitlist.drawer.validUntilRange')}</small>
                            )}
                            <div className="form-text">{t('waitlist.drawer.validUntilHint')}</div>
                        </div>

                      <fieldset disabled={fieldsLocked} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                        <div className="form-group">
                            <label>{t('waitlist.drawer.labelNotes')}</label>
                            <textarea
                                className="form-control"
                                data-cy="waitlist-notes-input"
                                rows={2}
                                value={draft.notes ?? ''}
                                onChange={(e) => updateDraft({ notes: e.target.value })}
                            />
                        </div>
                      </fieldset>
                    </form>
                )}
            </Drawer>

            {/* ----- Confirm modals ----- */}
            <ConfirmModal
                open={!!cancelTarget}
                title={t('waitlist.cancelModal.title')}
                danger
                busy={actionBusy}
                confirmLabel={t('waitlist.cancelModal.confirmLabel')}
                cancelLabel={t('waitlist.cancelModal.cancelLabel')}
                onConfirm={confirmCancel}
                onCancel={() => { if (!actionBusy) setCancelTarget(null); }}
                message={cancelTarget && t('waitlist.cancelModal.message', { name: clientLabel(cancelTarget) })}
            />
            <ConfirmModal
                open={!!deleteTarget}
                title={t('waitlist.deleteModal.title')}
                danger
                busy={actionBusy}
                confirmLabel={t('waitlist.deleteModal.confirmLabel')}
                onConfirm={confirmDelete}
                onCancel={() => { if (!actionBusy) setDeleteTarget(null); }}
                message={deleteTarget && t('waitlist.deleteModal.message', { name: clientLabel(deleteTarget) })}
            />
        </div>
    );
};

export default WaitlistComponent;
